import type { ComponentMeta } from "@mijalowcode/types";
import { ComponentCategory, PropType } from "@mijalowcode/types";

export const FormMeta: ComponentMeta = {
  componentName: "Form",
  package: "@mijadesign/web",
  title: "表单",
  description: "高性能表单控件，支持数据收集、校验和提交",
  category: ComponentCategory.FORM,
  icon: "FormOutlined",
  canHaveChildren: true,
  acceptedChildren: ["Form.Item", "Button", "Space", "Row", "Col", "Form.List"],
  designToken: {
    fields: [
      { name: "labelColor", label: "标签颜色", type: "color" },
      { name: "labelFontSize", label: "标签字号", type: "number", min: 10, max: 32 },
      { name: "itemMarginBottom", label: "表单项下间距", type: "number", min: 0, max: 64 },
    ],
  },
  props: [
    {
      name: "form",
      label: "表单实例",
      type: PropType.STRING,
      description: "表单实例名称，用于在操作中引用",
      group: "高级",
    },
    {
      name: "layout",
      label: "布局",
      type: PropType.ENUM,
      defaultValue: "horizontal",
      options: [
        { label: "水平", value: "horizontal" },
        { label: "垂直", value: "vertical" },
        { label: "行内", value: "inline" },
      ],
      group: "基础",
    },
    {
      name: "labelCol",
      label: "标签栅格",
      type: PropType.JSON,
      defaultValue: { span: 6 },
      description: "如 { span: 6 }",
      group: "布局",
    },
    {
      name: "wrapperCol",
      label: "控件栅格",
      type: PropType.JSON,
      defaultValue: { span: 18 },
      description: "如 { span: 18 }",
      group: "布局",
    },
    {
      name: "size",
      label: "尺寸",
      type: PropType.ENUM,
      defaultValue: "middle",
      options: [
        { label: "大", value: "large" },
        { label: "中", value: "middle" },
        { label: "小", value: "small" },
      ],
      group: "基础",
    },
    {
      name: "colon",
      label: "显示冒号",
      type: PropType.BOOLEAN,
      defaultValue: true,
      group: "基础",
    },
    {
      name: "labelAlign",
      label: "标签对齐",
      type: PropType.ENUM,
      defaultValue: "right",
      options: [
        { label: "左对齐", value: "left" },
        { label: "右对齐", value: "right" },
      ],
      group: "布局",
    },
    {
      name: "disabled",
      label: "禁用",
      type: PropType.BOOLEAN,
      defaultValue: false,
      group: "状态",
    },
    {
      name: "scrollToFirstError",
      label: "滚动到第一个错误",
      type: PropType.BOOLEAN,
      defaultValue: false,
      group: "验证",
    },
    {
      name: "hideRequiredMark",
      label: "隐藏必填标记",
      type: PropType.BOOLEAN,
      defaultValue: false,
      group: "验证",
    },
  ],
  defaultProps: {
    layout: "horizontal",
    labelCol: { span: 6 },
    wrapperCol: { span: 18 },
  },
  npm: {
    package: "@mijadesign/web",
    exportName: "Form",
  },
  snippet: {
    componentName: "Form",
    package: "@mijadesign/web",
    props: {
      layout: "horizontal",
      labelCol: { span: 6 },
      wrapperCol: { span: 18 },
    },
    children: [],
  },
};

export const FormItemMeta: ComponentMeta = {
  componentName: "Form.Item",
  package: "@mijadesign/web",
  title: "表单项",
  description: "表单字段容器，支持布局、校验和依赖",
  category: ComponentCategory.FORM,
  icon: "ContainerOutlined",
  canHaveChildren: true,
  childrenLimit: 1,
  props: [
    {
      name: "label",
      label: "标签",
      type: PropType.STRING,
      defaultValue: "字段",
      group: "基础",
    },
    {
      name: "name",
      label: "字段名",
      type: PropType.STRING,
      description: "表单字段的 name，用于数据收集",
      group: "基础",
    },
    {
      name: "required",
      label: "必填",
      type: PropType.BOOLEAN,
      defaultValue: false,
      group: "校验",
    },
    {
      name: "tooltip",
      label: "提示",
      type: PropType.STRING,
      group: "基础",
    },
    {
      name: "extra",
      label: "额外信息",
      type: PropType.STRING,
      description: "额外的提示信息",
      group: "基础",
    },
    {
      name: "labelCol",
      label: "标签栅格",
      type: PropType.JSON,
      description: "如 { span: 6 }",
      group: "布局",
    },
    {
      name: "wrapperCol",
      label: "控件栅格",
      type: PropType.JSON,
      description: "如 { span: 18 }",
      group: "布局",
    },
    {
      name: "colon",
      label: "显示冒号",
      type: PropType.BOOLEAN,
      group: "基础",
    },
    // 校验规则配置
    {
      name: "rules",
      label: "校验规则",
      type: PropType.JSON,
      description: '配置校验规则，如：[{ required: true, message: "必填" }]',
      group: "校验",
    },
    {
      name: "validateStatus",
      label: "校验状态",
      type: PropType.ENUM,
      options: [
        { label: "默认", value: "" },
        { label: "成功", value: "success" },
        { label: "警告", value: "warning" },
        { label: "错误", value: "error" },
        { label: "校验中", value: "validating" },
      ],
      group: "校验",
    },
    {
      name: "hasFeedback",
      label: "显示反馈图标",
      type: PropType.BOOLEAN,
      defaultValue: false,
      group: "校验",
    },
    // 依赖配置
    {
      name: "dependencies",
      label: "依赖字段",
      type: PropType.ARRAY,
      description: '依赖的其他字段，如：["otherField"]',
      group: "高级",
    },
    // 条件显示
    {
      name: "shouldUpdate",
      label: "条件渲染",
      type: PropType.BOOLEAN,
      description: "是否根据其他字段变化重新渲染",
      group: "高级",
    },
    {
      name: "noStyle",
      label: "无样式",
      type: PropType.BOOLEAN,
      defaultValue: false,
      description: "不包装样式，只进行数据收集",
      group: "高级",
    },
  ],
  defaultProps: {
    label: "字段",
  },
  npm: {
    package: "@mijadesign/web",
    exportName: "Form",
  },
  snippet: {
    componentName: "Form.Item",
    package: "@mijadesign/web",
    props: { label: "字段", name: "field" },
    children: [],
  },
};

export const FormListMeta: ComponentMeta = {
  componentName: "Form.List",
  package: "antd",
  title: "表单列表",
  description: "用于渲染一组表单字段，支持动态添加/删除",
  category: ComponentCategory.FORM,
  icon: "UnorderedListOutlined",
  canHaveChildren: true,
  props: [
    {
      name: "name",
      label: "字段名",
      type: PropType.STRING,
      required: true,
      description: "表单字段的 name",
      group: "基础",
    },
    {
      name: "initialValue",
      label: "初始值",
      type: PropType.ARRAY,
      group: "默认",
    },
    {
      name: "rules",
      label: "校验规则",
      type: PropType.JSON,
      group: "校验",
    },
  ],
  defaultProps: {},
  npm: {
    package: "antd",
    exportName: "Form",
  },
  snippet: {
    componentName: "Form.List",
    package: "antd",
    props: { name: "list" },
    children: [],
  },
};
